/**
 * Bucket filters from a list of edges (decision D-4, gap R8): Figma's six-bucket rows read
 * `<$100M`, `$100M–$250M`, …, `$2.5B+`. The edges come from the live distribution (Lane B), so
 * nothing here knows what a sensible threshold is — it only turns `n` ascending edges into `n + 1`
 * half-open ranges with the labels and URL keys the chips and the predicate share.
 */

/** @desc A bucket filter option: a half-open range `[min, max)` over one figure. */
export type BucketRange = { key: string; label: string; min: number; max: number };

/** @desc What wraps an edge in a label: `$` before, ` adults` after. */
export type BucketFormat = { prefix?: string; suffix?: string };

const grouped = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

const scaled = (value: number, unit: number, letter: string) => {
  const n = value / unit;
  const text = Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '');
  return `${text}${letter}`;
};

/**
 * @desc An edge as Figma prints it: `100M`, `2.5B`, `10K`, and plain grouped digits below ten
 * thousand (`5,000`, `10`) — the register's rows mix `<10K` with `$5,000+`.
 */
export const formatBucketEdge = (value: number): string => {
  if (!Number.isFinite(value) || value < 0) return '';
  if (value >= 1e9) return scaled(value, 1e9, 'B');
  if (value >= 1e6) return scaled(value, 1e6, 'M');
  if (value >= 1e4) return scaled(value, 1e3, 'K');
  return grouped.format(value);
};

/** @desc A URL-safe key for an edge: `100m`, `2.5b`, `5000`. */
const edgeKey = (value: number) => formatBucketEdge(value).replace(/,/g, '').toLowerCase();

/**
 * @desc `n` ascending edges → `n + 1` buckets: below the first, between each pair, and from the
 * last upwards. Labels carry the format (`<$100M`, `$100M–$250M`, `$2.5B+`); keys are stable
 * spellings of the edges (`lt100m`, `100m-250m`, `gte2.5b`) so a URL survives a relabel.
 */
export const bucketsFromEdges = (
  edges: readonly number[],
  { prefix = '', suffix = '' }: BucketFormat = {},
): BucketRange[] => {
  const sorted = [...edges].filter((e) => Number.isFinite(e) && e >= 0).sort((a, b) => a - b);
  if (sorted.length === 0) return [];
  const wrap = (edge: number) => `${prefix}${formatBucketEdge(edge)}${suffix}`;
  const first = sorted[0] ?? 0;
  const last = sorted[sorted.length - 1] ?? 0;
  const buckets: BucketRange[] = [
    { key: `lt${edgeKey(first)}`, label: `<${wrap(first)}`, min: 0, max: first },
  ];
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const min = sorted[i] ?? 0;
    const max = sorted[i + 1] ?? 0;
    buckets.push({
      key: `${edgeKey(min)}-${edgeKey(max)}`,
      label: `${wrap(min)}–${wrap(max)}`,
      min,
      max,
    });
  }
  buckets.push({ key: `gte${edgeKey(last)}`, label: `${wrap(last)}+`, min: last, max: Infinity });
  return buckets;
};

/** @desc Whether `value` falls in any of the chosen buckets (an empty choice matches nothing). */
export const inBuckets = (
  value: number,
  keys: readonly string[],
  buckets: readonly BucketRange[],
): boolean =>
  buckets.some(
    (b) => keys.includes(b.key) && value >= b.min && (value < b.max || b.max === Infinity),
  );
